import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Home, Mail, ExternalLink, Shield, Lock, Eye, Database, Users, Globe, FileText, AlertTriangle, Code } from 'lucide-react';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header Navigation */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 overflow-hidden">
                <img
                  src="/icon512_rounded.png"
                  alt="LinkBlitz Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                LinkBlitz Privacy Policy
              </h1>
            </div>
            <button
              onClick={scrollToTop}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-2 rounded-full font-semibold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-200"
              aria-label="Back to top"
            >
              ↑ Top
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
        {/* Back to Home Link */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-blue-600 hover:text-purple-600 font-medium mb-8 transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        {/* Page Header */}
        <div className="text-center mb-12 pb-8 border-b-2 border-gray-200">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 bg-clip-text text-transparent">
            LinkBlitz Privacy Policy
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-gray-600 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-semibold">Effective Date:</span>
              <span>November 14, 2025</span>
            </div>
            <div className="hidden sm:block w-1 h-1 bg-gray-400 rounded-full"></div>
            <div className="flex items-center gap-2">
              <span className="font-semibold">Last Updated:</span>
              <span>November 14, 2025</span>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-2 border-gray-100">
          <h2 className="text-2xl font-bold text-blue-600 mb-6 flex items-center gap-2">
            <FileText className="w-6 h-6" />
            Quick Navigation
          </h2>
          <nav className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { href: '#intro', label: 'Welcome to LinkBlitz', icon: <Shield /> },
              { href: '#section-1', label: 'Information We Collect', icon: <Database /> },
              { href: '#section-2', label: 'How We Use Your Information', icon: <Eye /> },
              { href: '#section-3', label: 'Data Storage and Security', icon: <Lock /> },
              { href: '#section-4', label: 'Data Sharing and Third Parties', icon: <Users /> },
              { href: '#section-5', label: 'Your Rights and Control', icon: <Shield /> },
              { href: '#section-6', label: 'LinkedIn Integration', icon: <Globe /> },
              { href: '#section-7', label: 'Cookies and Tracking', icon: <Database /> },
              { href: '#section-8', label: "Children's Privacy", icon: <Users /> },
              { href: '#section-9', label: 'International Users', icon: <Globe /> },
              { href: '#section-10', label: 'Policy Updates', icon: <FileText /> },
              { href: '#section-11', label: 'Contact Us', icon: <Mail /> },
              { href: '#section-12', label: 'Disclaimers', icon: <AlertTriangle /> },
              { href: '#section-13', label: 'Transparency', icon: <Code /> },
            ].map((item, index) => (
              <a
                key={index}
                href={item.href}
                className="flex items-center gap-2 text-blue-600 hover:text-purple-600 font-medium transition-colors duration-200 p-2 rounded-lg hover:bg-blue-50"
              >
                <span className="w-5 h-5 flex-shrink-0">{item.icon}</span>
                <span>{item.label}</span>
              </a>
            ))}
          </nav>
        </div>

        {/* Introduction Section */}
        <section id="intro" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-blue-500">
          <h2 className="text-3xl font-bold text-blue-600 mb-6 flex items-center gap-3">
            <Shield className="w-8 h-8" />
            Welcome to LinkBlitz
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              At LinkBlitz, we are committed to protecting your privacy and ensuring transparency about how we handle your data. This Privacy Policy explains what information we collect, how we use it, and your rights regarding your personal data.
            </p>
            <p>
              LinkBlitz is a Chrome extension designed to automate LinkedIn networking by helping you connect with multiple people efficiently. We built this tool with privacy at its core, and we want you to understand exactly how we operate.
            </p>
            <p className="font-semibold">
              By using LinkBlitz, you agree to the practices described in this Privacy Policy. If you do not agree with our policies and practices, please do not use our extension.
            </p>
          </div>
        </section>

        {/* Section 1: Information We Collect */}
        <section id="section-1" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-purple-500">
          <h2 className="text-3xl font-bold text-purple-600 mb-6 flex items-center gap-3">
            <Database className="w-8 h-8" />
            What Information We Collect
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>LinkBlitz is designed with a privacy-first approach. We collect minimal data necessary to provide our service.</p>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">✗ Information We DO NOT Collect</h3>
              <ul className="space-y-2 ml-6 list-disc">
                <li>We DO NOT collect your LinkedIn login credentials</li>
                <li>We DO NOT store your LinkedIn connection data on our servers</li>
                <li>We DO NOT track your browsing history outside of LinkedIn</li>
                <li>We DO NOT collect personally identifiable information (PII)</li>
                <li>We DO NOT sell, rent, or share your data with third parties</li>
                <li>We DO NOT use analytics or tracking cookies</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">✓ Information We DO Collect</h3>
              <ul className="space-y-2 ml-6 list-disc">
                <li><strong className="text-purple-600">Extension Settings:</strong> Your preferences such as the number of pages to process during auto-connect operations are stored locally in your browser using Chrome's storage API</li>
                <li><strong className="text-purple-600">LinkedIn Page Interactions:</strong> The extension interacts with LinkedIn pages you visit to automate connection requests, but this data remains in your browser session and is not transmitted to external servers</li>
                <li><strong className="text-purple-600">Error Logs:</strong> Basic error information may be logged locally to help improve the extension's functionality (these logs are never transmitted externally)</li>
              </ul>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg">
              <p className="font-semibold flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-600" />
                Local Storage Only:
              </p>
              <p className="mt-2">All data used by LinkBlitz is stored locally on your device using Chrome's local storage. No data is transmitted to external servers, databases, or third-party services.</p>
            </div>
          </div>
        </section>

        {/* Section 2: How We Use Your Information */}
        <section id="section-2" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-pink-500">
          <h2 className="text-3xl font-bold text-pink-600 mb-6 flex items-center gap-3">
            <Eye className="w-8 h-8" />
            How We Use Your Information
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>LinkBlitz uses the minimal data it collects solely to provide and improve the extension's functionality.</p>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Primary Use Cases</h3>
              <ul className="space-y-2 ml-6 list-disc">
                <li><strong className="text-pink-600">Automation:</strong> To execute automated connection requests on LinkedIn based on your input (search queries, page count)</li>
                <li><strong className="text-pink-600">User Preferences:</strong> To remember your settings (like the number of pages to process) for future sessions</li>
                <li><strong className="text-pink-600">Error Handling:</strong> To identify and fix bugs or issues in the extension</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">What We Do NOT Do</h3>
              <ul className="space-y-2 ml-6 list-disc">
                <li>We do not analyze your LinkedIn connections or network</li>
                <li>We do not create profiles or databases about you</li>
                <li>We do not use your data for advertising or marketing purposes</li>
                <li>We do not share your data with any third parties, including LinkedIn</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">LinkedIn API Compliance</h3>
              <p>LinkBlitz interacts with LinkedIn's user interface through content scripts. We do not use LinkedIn's official API, and we do not violate LinkedIn's terms of service intentionally. Users should be aware that automated actions on LinkedIn may be subject to LinkedIn's policies.</p>
            </div>
          </div>
        </section>

        {/* Section 3: Data Storage and Security */}
        <section id="section-3" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-blue-500">
          <h2 className="text-3xl font-bold text-blue-600 mb-6 flex items-center gap-3">
            <Lock className="w-8 h-8" />
            How We Protect Your Data
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>Your security is our priority. Here's how we protect your information:</p>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Local-Only Storage</h3>
              <p className="mb-2">All data is stored locally on your device using Chrome's secure storage API. This means:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Your data never leaves your computer</li>
                <li>No cloud storage or external databases are used</li>
                <li>You have complete control over your data</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No External Servers</h3>
              <p className="mb-2">LinkBlitz does not connect to any external servers or APIs (except for the standard LinkedIn website that you're already using). This means:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>No data transmission to third parties</li>
                <li>No risk of data breaches from our side</li>
                <li>Complete offline functionality after installation</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Chrome Security</h3>
              <p className="mb-2">We rely on Chrome's built-in security features:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Extension permissions are limited to only what's necessary (activeTab, scripting)</li>
                <li>Host permissions are restricted to linkedin.com only</li>
                <li>Manifest V3 compliance ensures modern security standards</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Encryption</h3>
              <p>Chrome automatically encrypts local storage data when you have device encryption enabled. We recommend keeping your device encrypted for maximum security.</p>
            </div>
          </div>
        </section>

        {/* Section 4: Data Sharing and Third Parties */}
        <section id="section-4" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-red-500">
          <h2 className="text-3xl font-bold text-red-600 mb-6 flex items-center gap-3">
            <Users className="w-8 h-8" />
            Data Sharing and Third Parties
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Third-Party Sharing</h3>
              <p className="font-bold text-red-600">LinkBlitz does not share, sell, rent, or trade your information with any third parties. Period.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Third-Party Services</h3>
              <p className="mb-2">We do not integrate with or use:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Analytics services (like Google Analytics)</li>
                <li>Advertising networks</li>
                <li>Social media tracking</li>
                <li>Third-party APIs (except LinkedIn's public web interface)</li>
                <li>External storage or cloud services</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Legal Compliance</h3>
              <p className="mb-2">We may be required to disclose information if required by law, such as in response to:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>A valid court order or subpoena</li>
                <li>Legal processes or government requests</li>
                <li>Protection against legal liability</li>
              </ul>
              <p className="mt-2 italic">However, since we don't collect or store personal data, there would be minimal information to disclose.</p>
            </div>
          </div>
        </section>

        {/* Section 5: Your Rights and Control */}
        <section id="section-5" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-green-500">
          <h2 className="text-3xl font-bold text-green-600 mb-6 flex items-center gap-3">
            <Shield className="w-8 h-8" />
            Your Rights and Control
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>You have complete control over your data and how LinkBlitz operates.</p>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Right to Access</h3>
              <p>Since all data is stored locally on your device, you can access it at any time through Chrome's developer tools or by inspecting the extension's local storage.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Right to Delete</h3>
              <p className="mb-2">You can delete all data associated with LinkBlitz by:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Uninstalling the extension from Chrome</li>
                <li>Clearing Chrome's extension data through browser settings</li>
                <li>Resetting the extension to default settings</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Right to Opt-Out</h3>
              <p className="mb-2">You can stop using LinkBlitz at any time by:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Disabling the extension</li>
                <li>Uninstalling the extension</li>
                <li>Not clicking the "Auto Connect" button</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Right to Data Portability</h3>
              <p>Since data is stored locally, you already have full access and can export it if needed through Chrome's storage API.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Right to Object</h3>
              <p>You can object to any data processing by simply not using the extension or by disabling specific features.</p>
            </div>
          </div>
        </section>

        {/* Section 6: LinkedIn Integration */}
        <section id="section-6" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-blue-500">
          <h2 className="text-3xl font-bold text-blue-600 mb-6 flex items-center gap-3">
            <Globe className="w-8 h-8" />
            LinkedIn Integration and Limitations
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Relationship with LinkedIn</h3>
              <p>LinkBlitz is an independent third-party Chrome extension and is <strong>not affiliated with, endorsed by, or officially connected to LinkedIn Corporation.</strong></p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">LinkedIn Terms of Service</h3>
              <p>Users are responsible for ensuring their use of LinkBlitz complies with LinkedIn's Terms of Service and User Agreement. Automated actions may be subject to LinkedIn's policies on automation and scraping.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Account Safety</h3>
              <p className="mb-2">While LinkBlitz is designed to operate safely, users should be aware that:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Excessive automated connection requests may trigger LinkedIn's rate limits</li>
                <li>LinkedIn may restrict or suspend accounts that violate their terms</li>
                <li>We recommend using LinkBlitz responsibly and within reasonable limits</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Guarantees</h3>
              <p className="mb-2">We do not guarantee that:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>LinkBlitz will work indefinitely (LinkedIn may change their interface)</li>
                <li>Your account will not be affected by using automated tools</li>
                <li>Connection requests will always be successful</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 7: Cookies and Tracking */}
        <section id="section-7" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-purple-500">
          <h2 className="text-3xl font-bold text-purple-600 mb-6 flex items-center gap-3">
            <Database className="w-8 h-8" />
            Cookies and Tracking Technologies
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Cookies</h3>
              <p>LinkBlitz does not set any cookies or use tracking technologies.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">LinkedIn Cookies</h3>
              <p>When you use LinkedIn, LinkedIn may set its own cookies. These are governed by LinkedIn's privacy policy, not ours. LinkBlitz does not interact with or access these cookies.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Browser Storage</h3>
              <p>LinkBlitz uses Chrome's local storage API (chrome.storage.local) to save your preferences. This is not a cookie and is only accessible by the extension itself.</p>
            </div>
          </div>
        </section>

        {/* Section 8: Children's Privacy */}
        <section id="section-8" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-pink-500">
          <h2 className="text-3xl font-bold text-pink-600 mb-6 flex items-center gap-3">
            <Users className="w-8 h-8" />
            Children's Privacy
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>LinkBlitz is not intended for use by individuals under the age of 16. We do not knowingly collect or solicit personal information from children. LinkedIn's minimum age requirement is 16 years old in most regions, and we expect users to comply with LinkedIn's age requirements.</p>
            <p>If we become aware that a child under 16 has provided us with personal information, we will take steps to delete such information.</p>
          </div>
        </section>

        {/* Section 9: International Users */}
        <section id="section-9" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-indigo-500">
          <h2 className="text-3xl font-bold text-indigo-600 mb-6 flex items-center gap-3">
            <Globe className="w-8 h-8" />
            International Data Transfers
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No International Transfers</h3>
              <p>Since LinkBlitz stores all data locally on your device, there are no international data transfers.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Global Availability</h3>
              <p>LinkBlitz is available worldwide through the Chrome Web Store. However, users must comply with their local laws regarding data privacy and LinkedIn usage.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">GDPR Compliance</h3>
              <p className="mb-2">For users in the European Union, LinkBlitz is designed to be GDPR compliant:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Minimal data collection (privacy by design)</li>
                <li>Local-only storage (data localization)</li>
                <li>User control over data (right to erasure)</li>
                <li>Transparency in data practices</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">CCPA Compliance</h3>
              <p className="mb-2">For users in California, USA:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>We do not sell personal information</li>
                <li>We do not share personal information for cross-context behavioral advertising</li>
                <li>You have the right to delete your information (by uninstalling the extension)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Section 10: Policy Updates */}
        <section id="section-10" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-orange-500">
          <h2 className="text-3xl font-bold text-orange-600 mb-6 flex items-center gap-3">
            <FileText className="w-8 h-8" />
            Updates to This Privacy Policy
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Policy Updates</h3>
              <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Notification of Changes</h3>
              <p className="mb-2">When we make significant changes to this Privacy Policy, we will:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Update the "Last Updated" date at the top of this page</li>
                <li>Notify users through the Chrome Web Store listing</li>
                <li>Include a changelog in the extension update notes</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Continued Use</h3>
              <p>Your continued use of LinkBlitz after any changes to this Privacy Policy constitutes your acceptance of the updated policy.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Review Regularly</h3>
              <p>We encourage you to review this Privacy Policy periodically to stay informed about how we protect your information.</p>
            </div>
          </div>
        </section>

        {/* Section 11: Contact Us */}
        <section id="section-11" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-blue-500">
          <h2 className="text-3xl font-bold text-blue-600 mb-6 flex items-center gap-3">
            <Mail className="w-8 h-8" />
            Contact Us
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or LinkBlitz's data practices, please contact us:</p>
            
            <ul className="space-y-2 ml-6 list-disc">
              <li><strong className="text-blue-600">Developer:</strong> Devansh Singh</li>
              <li>
                <strong className="text-blue-600">Website:</strong>{' '}
                <a 
                  href="https://devansh-singh-resume.vercel.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-purple-600 underline inline-flex items-center gap-1"
                >
                  https://devansh-singh-resume.vercel.app/
                  <ExternalLink className="w-4 h-4" />
                </a>
              </li>
              <li><strong className="text-blue-600">Extension:</strong> LinkBlitz v1.0</li>
              <li><strong className="text-blue-600">Support:</strong> Please visit the Chrome Web Store listing for support options</li>
            </ul>

            <p><strong>Response Time:</strong> We aim to respond to all inquiries within 7 business days.</p>
            <p><strong>User Feedback:</strong> We welcome your feedback and suggestions for improving LinkBlitz and our privacy practices.</p>
          </div>
        </section>

        {/* Section 12: Disclaimers */}
        <section id="section-12" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-yellow-500">
          <h2 className="text-3xl font-bold text-yellow-600 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-8 h-8" />
            Important Disclaimers
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Use at Your Own Risk</h3>
              <p>LinkBlitz is provided "as is" without warranties of any kind, express or implied. You use the extension at your own risk.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Liability for LinkedIn Actions</h3>
              <p className="mb-2">We are not responsible for:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>LinkedIn's actions regarding your account</li>
                <li>Changes to LinkedIn's interface that break functionality</li>
                <li>Connection requests that are ignored or rejected</li>
                <li>Any consequences of using automated tools on LinkedIn</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Guarantee of Service</h3>
              <p className="mb-2">We do not guarantee that:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>LinkBlitz will be error-free or uninterrupted</li>
                <li>The extension will meet your specific needs</li>
                <li>Future versions will maintain compatibility with older data</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Indemnification</h3>
              <p>By using LinkBlitz, you agree to indemnify and hold harmless the developer from any claims, damages, or expenses arising from your use of the extension.</p>
            </div>
          </div>
        </section>

        {/* Section 13: Transparency */}
        <section id="section-13" className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-8 border-l-4 border-green-500">
          <h2 className="text-3xl font-bold text-green-600 mb-6 flex items-center gap-3">
            <Code className="w-8 h-8" />
            Transparency and Open Source
          </h2>
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Code Transparency</h3>
              <p className="mb-2">We believe in transparency. If you want to inspect how LinkBlitz works, you can:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>View the source code by examining the extension files</li>
                <li>Inspect network requests (you'll see we make none except to LinkedIn)</li>
                <li>Review permissions in Chrome's extension management page</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Review</h3>
              <p>We encourage security researchers and developers to review our code and report any concerns.</p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Hidden Features</h3>
              <p className="mb-2">LinkBlitz does exactly what it says:</p>
              <ul className="space-y-2 ml-6 list-disc">
                <li>Automates LinkedIn connection requests</li>
                <li>Stores preferences locally</li>
                <li>Nothing more, nothing less</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Back to Top Button */}
        <div className="text-center mb-8">
          <button
            onClick={scrollToTop}
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-200 inline-flex items-center gap-2"
          >
            <Home className="w-5 h-5" />
            Back to Top
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-gray-200 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center space-y-6">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-12 h-12 overflow-hidden">
              <img
                src="/icon512_rounded.png"
                alt="LinkBlitz Logo"
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-xl font-bold text-gray-900">LinkBlitz</span>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <Link to="/" className="text-blue-600 hover:text-purple-600 font-medium flex items-center gap-1">
              <Home className="w-4 h-4" />
              Back to Home
            </Link>
            <span className="hidden sm:inline text-gray-400">•</span>
            <a
              href="https://devansh-singh-resume.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-purple-600 font-medium flex items-center gap-1"
            >
              Contact Developer
              <ExternalLink className="w-4 h-4" />
            </a>
            <span className="hidden sm:inline text-gray-400">•</span>
            <a
              href="https://chrome.google.com/webstore"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-purple-600 font-medium flex items-center gap-1"
            >
              Chrome Web Store
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="pt-6 border-t border-gray-200 space-y-2">
            <p className="text-sm text-gray-600">
              © 2025 <strong>LinkBlitz</strong> by Devansh Singh. All rights reserved.
            </p>
            <p className="text-sm text-gray-500">
              This Privacy Policy is effective as of <strong>November 14, 2025</strong>
            </p>
            <p className="text-sm text-gray-500">
              LinkBlitz is not affiliated with LinkedIn Corporation.
            </p>
            <p className="text-sm italic text-gray-700 flex items-center justify-center gap-2">
              Made with care for your privacy <Lock className="w-4 h-4" />
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
